<script setup lang="ts">
/**
 * Auth modal host — the frame every auth view is rendered into.
 *
 * Desktop is a 440px centred dialog; compact is a bottom sheet with a grabber
 * (Figma `66:46881` vs `66:47559`). The views themselves are identical between
 * the two — only this frame changes.
 */
import { computed, ref, watch } from 'vue'
import Drawer from 'primevue/drawer'
import { useAuthModal } from '@/features/auth/store'
import { useDevice } from '@/composables/useDevice'
import { FairDialog } from '@/ui/primitives/FairDialog'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import { useFairOverlay } from '@/ui/primitives/FairOverlay'
import fairLogoWordmarkUrl from '@/ui/icons/brand/fair-logo-wordmark.svg'
import './fair-auth-modal.css'

const auth = useAuthModal()
const { isDevice } = useDevice()

const authModalState = computed(() => auth.authModal.isShow)
const authModalCurrentView = computed(() => auth.authModal.currentView)
const visible = ref(false)

const isCompact = computed(() => isDevice.value === 'mobile')

// Desktop is a FairDialog, which registers itself; the compact sheet is a bare PrimeVue Drawer.
useFairOverlay(() => visible.value && isCompact.value)

function handleHideAuthModal() {
  useAuthModal().hideAuthModal()
}

// A route guard can open the modal before this component mounts (guest on
// /style-space → Home); without `immediate` that first `isShow` is never seen.
watch(
  () => authModalState.value,
  () => {
    visible.value = authModalState.value
  },
  { immediate: true },
)
</script>

<template>
  <Drawer
    v-if="isCompact"
    :visible="visible"
    class="fair-auth-sheet"
    position="bottom"
    :dismissable="true"
    :modal="true"
    :show-close-icon="false"
    :block-scroll="false"
    :pt="{
      root: { class: 'fair-auth-sheet' },
      mask: { class: 'fair-dialog-mask' },
    }"
    @update:visible="visible = $event"
    @hide="handleHideAuthModal"
  >
    <template #container>
      <div class="fair-auth-sheet__panel">
        <div
          class="fair-auth-sheet__grabber"
          aria-hidden="true"
        />

        <div class="fair-auth-modal__header">
          <img
            class="fair-auth-modal__logo"
            :src="fairLogoWordmarkUrl"
            alt="FAIR"
          >
          <FairIconButton
            class="fair-auth-modal__close"
            icon="close"
            size="medium"
            aria-label="Close"
            @click="handleHideAuthModal"
          />
        </div>

        <component
          :is="authModalCurrentView"
          @close-modal="handleHideAuthModal"
        />
      </div>
    </template>
  </Drawer>

  <FairDialog
    v-else
    v-model:visible="visible"
    class="fair-auth-modal"
    width="440px"
    :show-header="false"
    :show-footer="false"
    :closable="false"
    @close="handleHideAuthModal"
  >
    <div class="fair-auth-modal__header">
      <img
        class="fair-auth-modal__logo"
        :src="fairLogoWordmarkUrl"
        alt="FAIR"
      >
      <FairIconButton
        class="fair-auth-modal__close"
        icon="close"
        size="medium"
        aria-label="Close"
        @click="handleHideAuthModal"
      />
    </div>

    <component
      :is="authModalCurrentView"
      @close-modal="handleHideAuthModal"
    />
  </FairDialog>
</template>
